/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useRef,
  useState
} from 'react';
import {
  Grow,
  Paper,
  Popper
} from '@material-ui/core/';
import {makeStyles} from '@material-ui/core/styles';
import {isEnter, isTab} from '@ion/core/src/utils/KeyEventUtils';
import {ClickAwayListener} from '../components/listeners/ClickAwayListener';
import {DropdownArrowIcon} from '../icons/DropdownArrowIcon';
import {dlsZIndex} from '../theme/theme';
import {CheckboxState} from './HeaderCheckbox';
import IonCheckbox from './IonCheckbox';
import {SelectedRows} from './IonGridUtils';
import RowSelectionMenu from './RowSelectionMenu';

export interface DropdownCheckboxProps extends SelectedRows {
  pageSize?: number;
  toggleSelectingRows: (select?: boolean, applyToAllRows?: boolean) => void;
  checkboxState: CheckboxState;
}

const useStyles = makeStyles(() => ({
  caretWrapper : {
    paddingTop : '5px',
    cursor     : 'pointer'
  },
  headerCheckboxDropdownWrapper : {
    display    : 'flex',
    alignItems : 'center'
  },
  menuWrapper : {
    zIndex : dlsZIndex('rowSelectionMenuPopper')
  }
}));

/**
 * Checkbox component with a dropdown menu for selecting & deselecting grid page rows.
 *
 * ```typescript
 *
 *  import '@ion/dls/src/grid/';
 *
 *  <DropdownCheckbox checkboxState={{checked: true, indeterminate: false}}
 *    totalRowCount={totalRowCount}
 *    pageSize={pageSize}
 *    toggleSelectingPageRows={toggleSelectingPageRows} />
 *
 * ```
 */
export const DropdownCheckbox = ({
  totalRowCount,
  pageSize,
  checkboxState,
  setAreTotalRowsSelected,
  toggleSelectingRows
}: DropdownCheckboxProps) => {
  const classes         = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef       = useRef<HTMLDivElement>();

  const toggleDropdownMenu = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const closeDropdownMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const selectTotalRows = useCallback(() => {
    toggleSelectingRows(true, true);
    setAreTotalRowsSelected(true);
  }, []);

  const selectAllPageRows = useCallback(() => {
    toggleSelectingRows();
    setAreTotalRowsSelected(false);
  }, []);

  const deselectAllRows = useCallback(() => {
    toggleSelectingRows(false, true);
    setAreTotalRowsSelected(false);
  }, []);

  const onKeyDown = useCallback((event: KeyboardEvent): void => {
    if (isTab(event) || isEnter(event)) {
      event.preventDefault();
      setOpen(false);
    }
  }, []);

  return (
    <div className={classes.headerCheckboxDropdownWrapper}
      onClick={toggleDropdownMenu}
      ref={anchorRef}
      aria-controls={open ? 'menu-list-grow' : undefined}
      aria-haspopup="true" >
      <IonCheckbox checked={checkboxState.checked}
        indeterminate={checkboxState.indeterminate} />
      <span className={classes.caretWrapper}>
        <DropdownArrowIcon />
      </span>
      <Popper open={open}
        className={classes.menuWrapper}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        modifiers={{
          offset : {
            offset : '-15px'
          }
        }}
        placement={'bottom-start'}>
        {({TransitionProps}) => (
          <Grow {...TransitionProps}>
            <ClickAwayListener onClickAway={closeDropdownMenu} mountListener>
              <Paper>
                <RowSelectionMenu open={open}
                  onPageRowsSelect={selectAllPageRows}
                  onTotalRowsSelect={selectTotalRows}
                  onDeselectAllRows={deselectAllRows}
                  pageCount={pageSize}
                  totalRowCount={totalRowCount}
                  onKeyDown={onKeyDown} />
              </Paper>
            </ClickAwayListener>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default memo(DropdownCheckbox);
